import { Dispatch, SetStateAction } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import type { ChatMessage, AgentType, Attachment } from '../types/chat';
import { validateFileSize, validateFileType } from '../config/attachmentConfig';
import { ellisDntChatApiRef } from '../../../apis/ellisDntChatApi';

type Message = ChatMessage;

interface ChatHistoryItem {
  id: string;
  title: string;
  agent: AgentType;
  messages: Message[];
}

interface UseChatBotActionsParams {
  messages: Message[];
  setMessages: Dispatch<SetStateAction<Message[]>>;
  currentAgent: AgentType;
  setCurrentAgent: (agent: AgentType) => void;
  setCurrentChatId: (id: string | null) => void;
  addToHistory: (
    title: string,
    agent: AgentType,
    messages: Message[],
  ) => string;
  loadChatHistory: (chatId: string) => ChatHistoryItem | undefined;
  sendMessage: (content: string, attachments?: Attachment[]) => Promise<void>;
  stopMessage: () => void;
  agents: Array<{ id: AgentType; name: string }>;
  updateChatMessages?: (chatId: string, messages: Message[]) => void;
  userEmail: string;
}

interface UseChatBotActionsReturn {
  handleSendMessage: (content: string, attachments?: File[]) => Promise<void>;
  handleNewChat: () => void;
  handleSelectAgent: (agentId: AgentType) => void;
  handleLoadChatHistory: (chatId: string) => void;
  handleRegenerate: () => Promise<void>;
  handleEdit: (editedContent: string) => void;
}

// Helper function to convert File to Base64
const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Extract base64 data (remove data:...;base64, prefix)
      const base64Data = result.split(',')[1];
      resolve(base64Data);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

// Helper function to reconstruct blob URL from base64 data
const base64ToBlob = (base64Data: string, mimeType: string): Blob => {
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: mimeType });
};

// Helper function to create blob URL from attachment
const createBlobUrlFromAttachment = (attachment: Attachment): string => {
  if (attachment.base64Data && attachment.mimeType) {
    const blob = base64ToBlob(attachment.base64Data, attachment.mimeType);
    return URL.createObjectURL(blob);
  }
  return attachment.url;
};

export const useChatBotActions = ({
  messages,
  setMessages,
  currentAgent,
  setCurrentAgent,
  setCurrentChatId,
  addToHistory,
  loadChatHistory,
  sendMessage,
  stopMessage,
  agents,
  updateChatMessages,
  userEmail,
}: UseChatBotActionsParams): UseChatBotActionsReturn => {
  const ellisDntChatApi = useApi(ellisDntChatApiRef);

  const handleSendMessage = async (content: string, files?: File[]) => {
    if (!content.trim()) return;

    // Check if this is the first message in a new conversation
    const isFirstMessage = messages.length === 0;

    // Upload files to SharePoint and create attachments with SharePoint URLs
    let attachments: Attachment[] | undefined;
    if (files && files.length > 0) {
      attachments = await Promise.all(
        files.map(async (file, index) => {
          const sharepointResponse =
            await ellisDntChatApi.uploadFileToSharePoint(file, userEmail);

          const base64Data = sharepointResponse?.file_url
            ? undefined
            : await fileToBase64(file);

          const attachment: Attachment = {
            id: sharepointResponse?.file_id || `${Date.now()}-${index}`,
            name: file.name,
            type: file.type.startsWith('image/') ? 'image' : 'document',
            url: sharepointResponse?.file_url
              ? sharepointResponse.file_url
              : URL.createObjectURL(file),
            size: file.size,
            base64Data,
            mimeType: file.type,
            sharepointFileId: sharepointResponse?.file_id,
            sharepointUrl: sharepointResponse?.file_url,
          };

          return attachment;
        }),
      );
    }

    // Add user message to state with attachments
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content,
      agent: currentAgent,
      timestamp: Date.now(),
      attachments,
    };

    setMessages(prev => [...prev, userMsg]);

    // If first message, add to history immediately
    if (isFirstMessage) {
      const title =
        content.substring(0, 40) + (content.length > 40 ? '...' : '');
      const chatId = addToHistory(title, currentAgent, [userMsg]);
      setCurrentChatId(chatId);
    }

    // Send message via chat hook (handles API call and streaming)
    await sendMessage(content, attachments);
  };

  const handleNewChat = () => {
    stopMessage();
    setMessages([]);
    setCurrentChatId(null);
  };

  const handleSelectAgent = (agentId: AgentType) => {
    // Clear current chat when switching agents
    setCurrentAgent(agentId);
    setMessages([]);
    setCurrentChatId(null);
  };

  const handleLoadChatHistory = (chatId: string) => {
    stopMessage();
    const chat = loadChatHistory(chatId);
    if (chat) {
      setCurrentAgent(chat.agent);
      // Reconstruct blob URLs from base64 data for attachments
      const messagesWithValidUrls = chat.messages.map(msg => {
        if (msg.attachments && msg.attachments.length > 0) {
          return {
            ...msg,
            attachments: msg.attachments.map(attachment => ({
              ...attachment,
              url: createBlobUrlFromAttachment(attachment),
            })),
          };
        }
        return msg;
      });
      // Load the messages from history
      setMessages(messagesWithValidUrls);
      setCurrentChatId(chatId);
    }
  };

  const handleRegenerate = async () => {
    if (messages.length < 2) return;

    // Find the last user message
    const lastUserMessageIndex = messages.findIndex(
      (msg, idx) => idx === messages.length - 2 && msg.role === 'user',
    );

    if (lastUserMessageIndex === -1) {
      // If not found in expected position, search backwards
      for (let i = messages.length - 2; i >= 0; i--) {
        if (messages[i].role === 'user') {
          const userContent = messages[i].content;
          // Remove the last AI message and resend
          setMessages(prev => prev.slice(0, -1));
          await sendMessage(userContent);
          return;
        }
      }
    } else {
      const userContent = messages[lastUserMessageIndex].content;
      // Remove the last AI message and resend
      setMessages(prev => prev.slice(0, -1));
      await sendMessage(userContent);
    }
  };

  const handleEdit = (editedContent: string) => {
    if (!editedContent.trim()) return;

    // Find the last user message
    const lastUserMessageIndex = messages
      .slice()
      .reverse()
      .findIndex(msg => msg.role === 'user');

    if (lastUserMessageIndex === -1) return;

    const userMessageIndex = messages.length - 1 - lastUserMessageIndex;

    // Just update the user message with edited content, don't delete anything
    setMessages(prev => {
      const updated = [...prev];
      updated[userMessageIndex] = {
        ...updated[userMessageIndex],
        content: editedContent,
      };
      return updated;
    });
  };

  return {
    handleSendMessage,
    handleNewChat,
    handleSelectAgent,
    handleLoadChatHistory,
    handleRegenerate,
    handleEdit,
  };
};
